import { useCallback, useEffect, useRef, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "@shared/services/apiClient";
import type { ChannelPair as EditableChannelPair } from "@features/Montage/components/EditChannelPairModal";
import {
  fetchMontageByIdWithMeta,
  updateMontage,
} from "@features/Montage/services/MontageService";

/** Minimal montage shape used by viewer draft editing (matches EEGViewer). */
export type EEGViewerMontage = {
  montage_id?: string;
  montage_name?: string;
  montage_type?: string;
  is_shared?: boolean;
  lock_version?: string | number;
  channel_pairs?: Array<{
    positive: string;
    negative?: string;
    label?: string;
    color?: string;
  }>;
};
import { normalizeHexColor } from "../types/eegViewerTraceTypes";
import {
  defaultMontagePairLabel,
  normalizeMontageDraftPair,
  type ViewerMontageDraftPair,
} from "../utils/eegViewerMontageDraft";

export type { ViewerMontageDraftPair } from "../utils/eegViewerMontageDraft";

function toUserFacingApiError(error: unknown): {
  status: number | null;
  message: string;
} {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  const status = axiosError?.response?.status ?? null;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    axiosError?.message ||
    "Request failed.";
  return { status, message };
}

export function useEEGViewerMontageDraft(params: {
  currentMontage: EEGViewerMontage | null | undefined;
  montageAvailableElectrodesRef: React.MutableRefObject<string[]>;
  selectedMontagePairIndexesRef: React.MutableRefObject<number[]>;
  setSelectedTraceChannelsRef: React.MutableRefObject<
    ((labels: string[]) => void) | null
  >;
  lastTraceSelectionRef: React.MutableRefObject<string | null>;
  getMontageChannelLabel: (pair: ViewerMontageDraftPair) => string;
  queryClient: QueryClient;
}) {
  const {
    currentMontage,
    montageAvailableElectrodesRef,
    selectedMontagePairIndexesRef,
    setSelectedTraceChannelsRef,
    lastTraceSelectionRef,
    getMontageChannelLabel,
    queryClient,
  } = params;

  const [viewerMontagePairsDraft, setViewerMontagePairsDraft] = useState<
    ViewerMontageDraftPair[]
  >([]);
  const [viewerMontagePairsById, setViewerMontagePairsById] = useState<
    Record<string, ViewerMontageDraftPair[]>
  >({});
  const [viewerMontageDirtyById, setViewerMontageDirtyById] = useState<
    Record<string, boolean>
  >({});
  const [viewerMontageBaseLockVersionById, setViewerMontageBaseLockVersionById] =
    useState<Record<string, string>>({});
  const [viewerMontageEtagById, setViewerMontageEtagById] = useState<
    Record<string, string | null>
  >({});
  const [viewerMontageDraftDirty, setViewerMontageDraftDirty] = useState(false);
  const [viewerMontageStatus, setViewerMontageStatus] = useState<string | null>(
    null,
  );
  const [viewerMontageError, setViewerMontageError] = useState<string | null>(
    null,
  );
  const [viewerMontageConflict, setViewerMontageConflict] = useState<
    string | null
  >(null);
  const [viewerMontageConflictRemote, setViewerMontageConflictRemote] =
    useState<EEGViewerMontage | null>(null);
  const [isSavingViewerMontage, setIsSavingViewerMontage] = useState(false);
  const [editingMontagePairIndex, setEditingMontagePairIndex] = useState<
    number | null
  >(null);
  const [showMontageMobileSheet, setShowMontageMobileSheet] = useState(false);
  const [montageNotice, setMontageNotice] = useState<string | null>(null);
  const montageNoticeTimeoutRef = useRef<number | null>(null);

  const announceMontageChange = useCallback(
    (montageName: string, detail?: string) => {
      setMontageNotice(detail?.trim() || `Montage: ${montageName}`);
      if (montageNoticeTimeoutRef.current !== null) {
        window.clearTimeout(montageNoticeTimeoutRef.current);
      }
      montageNoticeTimeoutRef.current = window.setTimeout(() => {
        setMontageNotice(null);
        montageNoticeTimeoutRef.current = null;
      }, 4500);
    },
    [],
  );

  useEffect(
    () => () => {
      if (montageNoticeTimeoutRef.current !== null) {
        window.clearTimeout(montageNoticeTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const montageId = currentMontage?.montage_id;
    if (!montageId) {
      setViewerMontagePairsDraft([]);
      setViewerMontageDraftDirty(false);
      return;
    }

    const persistedDraft = viewerMontagePairsById[montageId];
    if (persistedDraft) {
      setViewerMontagePairsDraft(persistedDraft);
    } else {
      const normalizedPairs = (currentMontage.channel_pairs || []).map(
        (pair, index) => normalizeMontageDraftPair(pair, index),
      );
      setViewerMontagePairsById((current) => ({
        ...current,
        [montageId]: normalizedPairs,
      }));
      setViewerMontagePairsDraft(normalizedPairs);
    }

    setViewerMontageDraftDirty(!!viewerMontageDirtyById[montageId]);
    setViewerMontageStatus(null);
    setViewerMontageError(null);
    setViewerMontageConflict(null);
    setViewerMontageConflictRemote(null);
    setEditingMontagePairIndex(null);
    setShowMontageMobileSheet(false);

    const lockVersionRaw = (currentMontage as { lock_version?: unknown })
      .lock_version;
    const lockVersion =
      lockVersionRaw !== undefined && lockVersionRaw !== null
        ? String(lockVersionRaw)
        : null;
    if (lockVersion && !viewerMontageBaseLockVersionById[montageId]) {
      setViewerMontageBaseLockVersionById((current) => ({
        ...current,
        [montageId]: lockVersion,
      }));
    }
    if (lockVersion && !viewerMontageEtagById[montageId]) {
      setViewerMontageEtagById((current) => ({
        ...current,
        [montageId]: `"${lockVersion}"`,
      }));
    }
  }, [
    currentMontage,
    viewerMontageBaseLockVersionById,
    viewerMontageDirtyById,
    viewerMontageEtagById,
    viewerMontagePairsById,
  ]);

  const createViewerMontagePair = useCallback(
    (
      base?: Partial<{
        positive: string;
        negative: string;
        label: string;
        color: string;
      }>,
    ): ViewerMontageDraftPair => {
      const positive = (
        base?.positive ||
        montageAvailableElectrodesRef.current[0] ||
        ""
      ).trim();
      const fallbackNegative =
        montageAvailableElectrodesRef.current.find(
          (electrode) => electrode !== positive,
        ) ||
        montageAvailableElectrodesRef.current[1] ||
        "";
      const negative = (base?.negative || fallbackNegative).trim();
      const label =
        (base?.label || "").trim() ||
        defaultMontagePairLabel({ positive, negative }) ||
        "New Pair";

      const normalizedColor = normalizeHexColor(base?.color);
      return {
        positive,
        negative,
        label,
        ...(normalizedColor ? { color: normalizedColor } : {}),
      };
    },
    [montageAvailableElectrodesRef],
  );

  const applyViewerMontageDraft = useCallback(
    (
      nextPairs: Array<{
        positive?: string;
        negative?: string;
        label?: string;
        color?: string;
      }>,
      markDirty = true,
    ) => {
      const montageId = currentMontage?.montage_id;
      if (!montageId) return;

      const normalizedPairs = nextPairs.map((pair, index) =>
        normalizeMontageDraftPair(pair, index),
      );

      setViewerMontagePairsDraft(normalizedPairs);
      setViewerMontagePairsById((current) => ({
        ...current,
        [montageId]: normalizedPairs,
      }));
      if (markDirty) {
        setViewerMontageDraftDirty(true);
        setViewerMontageDirtyById((current) => ({
          ...current,
          [montageId]: true,
        }));
      }
      setViewerMontageStatus(null);
      setViewerMontageError(null);
      setViewerMontageConflict(null);
      setViewerMontageConflictRemote(null);
    },
    [currentMontage?.montage_id],
  );

  const handleInsertViewerMontagePair = useCallback(() => {
    if (!currentMontage?.montage_id) {
      setViewerMontageStatus(null);
      setViewerMontageError("No active montage selected.");
      return;
    }
    const selectedMontagePairIndexes = selectedMontagePairIndexesRef.current;
    const insertAt =
      selectedMontagePairIndexes.length > 0
        ? Math.max(...selectedMontagePairIndexes) + 1
        : viewerMontagePairsDraft.length;
    const nextPairs = [...viewerMontagePairsDraft];
    nextPairs.splice(insertAt, 0, createViewerMontagePair());
    applyViewerMontageDraft(nextPairs);
    setViewerMontageStatus(
      `Inserted channel pair at position ${insertAt + 1}.`,
    );
  }, [
    applyViewerMontageDraft,
    createViewerMontagePair,
    currentMontage?.montage_id,
    selectedMontagePairIndexesRef,
    viewerMontagePairsDraft,
  ]);

  const handleDuplicateViewerMontagePair = useCallback(() => {
    const selectedMontagePairIndexes = selectedMontagePairIndexesRef.current;
    if (!viewerMontagePairsDraft.length) {
      setViewerMontageStatus(null);
      setViewerMontageError("No montage pairs available to duplicate.");
      return;
    }
    const sourceIndex =
      selectedMontagePairIndexes.length > 0
        ? selectedMontagePairIndexes[0]
        : viewerMontagePairsDraft.length - 1;
    const sourcePair = viewerMontagePairsDraft[sourceIndex];
    if (!sourcePair) return;

    const duplicate = createViewerMontagePair({
      ...sourcePair,
      label: `${sourcePair.label || defaultMontagePairLabel(sourcePair)} Copy`,
    });
    const nextPairs = [...viewerMontagePairsDraft];
    nextPairs.splice(sourceIndex + 1, 0, duplicate);
    applyViewerMontageDraft(nextPairs);
    setViewerMontageStatus(`Duplicated pair #${sourceIndex + 1}.`);
  }, [
    applyViewerMontageDraft,
    createViewerMontagePair,
    selectedMontagePairIndexesRef,
    viewerMontagePairsDraft,
  ]);

  const handleMoveViewerMontagePair = useCallback(
    (direction: "up" | "down") => {
      const selectedMontagePairIndexes = selectedMontagePairIndexesRef.current;
      if (
        !viewerMontagePairsDraft.length ||
        selectedMontagePairIndexes.length === 0
      ) {
        setViewerMontageStatus(null);
        setViewerMontageError("Select a channel pair to move.");
        return;
      }

      const sourceIndex = selectedMontagePairIndexes[0];
      const targetIndex =
        direction === "up" ? sourceIndex - 1 : sourceIndex + 1;
      if (targetIndex < 0 || targetIndex >= viewerMontagePairsDraft.length) {
        return;
      }

      const nextPairs = [...viewerMontagePairsDraft];
      const [moved] = nextPairs.splice(sourceIndex, 1);
      nextPairs.splice(targetIndex, 0, moved);
      applyViewerMontageDraft(nextPairs);

      const movedLabel = getMontageChannelLabel(moved);
      setSelectedTraceChannelsRef.current?.([movedLabel]);
      lastTraceSelectionRef.current = movedLabel;
      setViewerMontageStatus(
        `Moved pair from position ${sourceIndex + 1} to ${targetIndex + 1}.`,
      );
    },
    [
      applyViewerMontageDraft,
      getMontageChannelLabel,
      lastTraceSelectionRef,
      selectedMontagePairIndexesRef,
      setSelectedTraceChannelsRef,
      viewerMontagePairsDraft,
    ],
  );

  const handleDeleteViewerMontagePairs = useCallback(() => {
    const selectedMontagePairIndexes = selectedMontagePairIndexesRef.current;
    if (
      !viewerMontagePairsDraft.length ||
      selectedMontagePairIndexes.length === 0
    ) {
      setViewerMontageStatus(null);
      setViewerMontageError("Select one or more channel pairs to delete.");
      return;
    }

    const indexSet = new Set(selectedMontagePairIndexes);
    const nextPairs = viewerMontagePairsDraft.filter(
      (_, index) => !indexSet.has(index),
    );
    applyViewerMontageDraft(nextPairs);
    setSelectedTraceChannelsRef.current?.([]);
    lastTraceSelectionRef.current = null;
    setViewerMontageStatus(
      `Deleted ${selectedMontagePairIndexes.length} pair(s).`,
    );
  }, [
    applyViewerMontageDraft,
    lastTraceSelectionRef,
    selectedMontagePairIndexesRef,
    setSelectedTraceChannelsRef,
    viewerMontagePairsDraft,
  ]);

  const handleOpenViewerMontagePairEditor = useCallback(() => {
    const selectedMontagePairIndexes = selectedMontagePairIndexesRef.current;
    if (selectedMontagePairIndexes.length === 0) {
      setViewerMontageStatus(null);
      setViewerMontageError("Select a channel pair to edit.");
      return;
    }
    setViewerMontageError(null);
    setEditingMontagePairIndex(selectedMontagePairIndexes[0]);
  }, [selectedMontagePairIndexesRef]);

  const handleSaveViewerMontagePairEdit = useCallback(
    (updatedPair: EditableChannelPair) => {
      if (editingMontagePairIndex === null) return;
      const updatedDraftPair = normalizeMontageDraftPair(
        {
          positive: updatedPair.positiveElectrode,
          negative: updatedPair.negativeElectrode,
          label: updatedPair.label,
          color: updatedPair.color,
        },
        editingMontagePairIndex,
      );
      const nextPairs = [...viewerMontagePairsDraft];
      nextPairs.splice(editingMontagePairIndex, 1, updatedDraftPair);
      applyViewerMontageDraft(nextPairs);
      setViewerMontageStatus(`Updated pair #${editingMontagePairIndex + 1}.`);
      setEditingMontagePairIndex(null);
    },
    [applyViewerMontageDraft, editingMontagePairIndex, viewerMontagePairsDraft],
  );

  const persistViewerMontageDraft = useCallback(
    async (forceOverwrite: boolean) => {
      if (!currentMontage?.montage_id) {
        setViewerMontageStatus(null);
        setViewerMontageError("No active montage selected.");
        return;
      }
      if (!viewerMontageDraftDirty) {
        setViewerMontageError(null);
        setViewerMontageStatus("No unsaved montage changes.");
        return;
      }

      const montageId = currentMontage.montage_id;
      const sanitizedPairs = viewerMontagePairsDraft
        .map((pair, index) => normalizeMontageDraftPair(pair, index))
        .filter((pair) => pair.positive && pair.positive !== pair.negative);

      if (!sanitizedPairs.length) {
        setViewerMontageStatus(null);
        setViewerMontageError(
          "Montage must contain at least one valid channel pair.",
        );
        return;
      }

      setIsSavingViewerMontage(true);
      setViewerMontageError(null);
      setViewerMontageStatus(null);

      try {
        const remoteSnapshot = await fetchMontageByIdWithMeta(montageId);
        const remoteMontage = remoteSnapshot.montage as EEGViewerMontage;
        const remoteLockVersion =
          remoteSnapshot.lockVersion ||
          ((remoteMontage as { lock_version?: unknown }).lock_version !==
            undefined &&
          (remoteMontage as { lock_version?: unknown }).lock_version !== null
            ? String((remoteMontage as { lock_version?: unknown }).lock_version)
            : "");
        const baseLockVersion =
          viewerMontageBaseLockVersionById[montageId] ||
          ((currentMontage as { lock_version?: unknown }).lock_version !==
            undefined &&
          (currentMontage as { lock_version?: unknown }).lock_version !== null
            ? String((currentMontage as { lock_version?: unknown }).lock_version)
            : "");

        if (
          !forceOverwrite &&
          remoteMontage.is_shared &&
          remoteLockVersion &&
          baseLockVersion &&
          remoteLockVersion !== baseLockVersion
        ) {
          setViewerMontageConflict(
            "Shared montage changed since you started editing (version mismatch). Reload remote or overwrite with your draft.",
          );
          setViewerMontageConflictRemote(remoteMontage);
          setViewerMontageEtagById((current) => ({
            ...current,
            [montageId]: remoteSnapshot.etag,
          }));
          return;
        }
        const expectedVersion = forceOverwrite
          ? remoteLockVersion || null
          : baseLockVersion || remoteLockVersion || null;
        const ifMatchValue = forceOverwrite
          ? remoteSnapshot.etag ||
            (expectedVersion ? `"${expectedVersion}"` : null)
          : viewerMontageEtagById[montageId] ||
            (expectedVersion ? `"${expectedVersion}"` : null);

        const savedMontage = await updateMontage(
          montageId,
          {
            channel_pairs: sanitizedPairs.map((pair) => ({
              positive: pair.positive,
              negative: pair.negative,
              label: pair.label,
              ...(pair.color ? { color: pair.color } : {}),
            })),
          },
          {
            ifMatch: ifMatchValue,
            expectedVersion,
          },
        );

        const normalizedSavedPairs = (savedMontage.channel_pairs || []).map(
          (pair, index) => normalizeMontageDraftPair(pair, index),
        );
        setViewerMontagePairsById((current) => ({
          ...current,
          [montageId]: normalizedSavedPairs,
        }));
        setViewerMontagePairsDraft(normalizedSavedPairs);
        setViewerMontageDraftDirty(false);
        setViewerMontageDirtyById((current) => ({
          ...current,
          [montageId]: false,
        }));
        setViewerMontageConflict(null);
        setViewerMontageConflictRemote(null);
        setViewerMontageStatus(
          `Saved ${normalizedSavedPairs.length} channel pair(s) to "${savedMontage.montage_name}".`,
        );
        const savedLockVersionRaw = (savedMontage as { lock_version?: unknown })
          .lock_version;
        const savedLockVersion =
          savedLockVersionRaw !== undefined && savedLockVersionRaw !== null
            ? String(savedLockVersionRaw)
            : expectedVersion
              ? /^\d+$/.test(String(expectedVersion))
                ? (BigInt(String(expectedVersion)) + 1n).toString()
                : null
              : remoteLockVersion;
        if (savedLockVersion) {
          setViewerMontageBaseLockVersionById((current) => ({
            ...current,
            [montageId]: savedLockVersion,
          }));
          setViewerMontageEtagById((current) => ({
            ...current,
            [montageId]: `"${savedLockVersion}"`,
          }));
        }
        await queryClient.invalidateQueries({ queryKey: ["eeg-montages"] });
      } catch (error) {
        const normalizedError = toUserFacingApiError(error);
        const conflictPayload = (
          error as AxiosError<{
            data?: {
              current_lock_version?: string;
              current_montage?: EEGViewerMontage;
              current_etag?: string | null;
            };
          }>
        )?.response?.data?.data;

        if (normalizedError.status === 409 && conflictPayload) {
          setViewerMontageConflict(
            "Montage update conflict detected. Reload remote or overwrite.",
          );
          if (conflictPayload.current_montage) {
            setViewerMontageConflictRemote(conflictPayload.current_montage);
          }
          if (conflictPayload.current_etag) {
            setViewerMontageEtagById((current) => ({
              ...current,
              [montageId]: conflictPayload.current_etag || null,
            }));
          }
          if (conflictPayload.current_lock_version) {
            setViewerMontageBaseLockVersionById((current) => ({
              ...current,
              [montageId]: conflictPayload.current_lock_version!,
            }));
          }
          setViewerMontageError(null);
        } else {
          setViewerMontageError(normalizedError.message);
        }
      } finally {
        setIsSavingViewerMontage(false);
      }
    },
    [
      currentMontage,
      queryClient,
      viewerMontageBaseLockVersionById,
      viewerMontageDraftDirty,
      viewerMontageEtagById,
      viewerMontagePairsDraft,
    ],
  );

  const handleSaveViewerMontageDraft = useCallback(() => {
    void persistViewerMontageDraft(false);
  }, [persistViewerMontageDraft]);

  const handleOverwriteViewerMontageDraft = useCallback(() => {
    void persistViewerMontageDraft(true);
  }, [persistViewerMontageDraft]);

  const handleReloadViewerMontageConflictRemote = useCallback(() => {
    const montageId = currentMontage?.montage_id;
    if (!montageId || !viewerMontageConflictRemote) return;

    const remotePairs = (viewerMontageConflictRemote.channel_pairs || []).map(
      (pair, index) => normalizeMontageDraftPair(pair, index),
    );
    setViewerMontagePairsById((current) => ({
      ...current,
      [montageId]: remotePairs,
    }));
    setViewerMontagePairsDraft(remotePairs);
    setViewerMontageDraftDirty(false);
    setViewerMontageDirtyById((current) => ({
      ...current,
      [montageId]: false,
    }));
    const remoteLockVersionRaw = (
      viewerMontageConflictRemote as { lock_version?: unknown }
    ).lock_version;
    if (remoteLockVersionRaw !== undefined && remoteLockVersionRaw !== null) {
      const remoteLockVersion = String(remoteLockVersionRaw);
      setViewerMontageBaseLockVersionById((current) => ({
        ...current,
        [montageId]: remoteLockVersion,
      }));
      setViewerMontageEtagById((current) => ({
        ...current,
        [montageId]: `"${remoteLockVersion}"`,
      }));
    }
    setViewerMontageConflict(null);
    setViewerMontageConflictRemote(null);
    setViewerMontageError(null);
    setViewerMontageStatus("Reloaded latest shared montage revision.");
  }, [currentMontage?.montage_id, viewerMontageConflictRemote]);

  return {
    viewerMontagePairsById,
    viewerMontagePairsDraft,
    viewerMontageDraftDirty,
    viewerMontageStatus,
    viewerMontageError,
    viewerMontageConflict,
    viewerMontageConflictRemote,
    isSavingViewerMontage,
    editingMontagePairIndex,
    setEditingMontagePairIndex,
    showMontageMobileSheet,
    setShowMontageMobileSheet,
    montageNotice,
    announceMontageChange,
    handleInsertViewerMontagePair,
    handleDuplicateViewerMontagePair,
    handleMoveViewerMontagePair,
    handleDeleteViewerMontagePairs,
    handleOpenViewerMontagePairEditor,
    handleSaveViewerMontagePairEdit,
    handleSaveViewerMontageDraft,
    handleOverwriteViewerMontageDraft,
    handleReloadViewerMontageConflictRemote,
  };
}
